import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Aviso, Botao, Campo, CampoDeTexto, Modal, formatarMoeda } from '../../../componentes/ui';
import { formatarCnpj, formatarCompetencia } from '../../../servicos/formato';
import {
  CHAVE_DA_PROSPECCAO,
  MODALIDADES,
  cadastrarEmpresaDaProspeccao,
  cadastrarPessoaDaProspeccao,
  carregarCadastroDaEmpresa,
  historicoDaEmpresa,
  rotuloDoMes,
  salvarAnotacao,
  type AnotacaoDaEmpresa,
  type AnotacaoParaSalvar,
  type ContatoSiscred,
  type EmpresaProspectada,
} from '../../../servicos/prospeccao';
import { ErroDaApi } from '../../ErroDaApi';
import { NoCadastroDoDeca } from './NoCadastroDoDeca';

type ContatoEditavel = { -readonly [K in keyof ContatoSiscred]: string };

const CONTATO_VAZIO: ContatoEditavel = { responsavel: '', cargo: '', telefone: '', email: '' };

const paraTexto = (v: string | null): string => v ?? '';
const paraValor = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/**
 * A empresa da coleta: o que o SISCRED mostrou no mês (só leitura) e as
 * anotações da operação (editáveis), que valem em todos os meses.
 *
 * As anotações são as colunas que a planilha tinha — responsável, cargo,
 * telefone e e-mail de cada contato, deságio por modalidade e atualizações —, para quem vem da planilha achar tudo no mesmo lugar.
 *
 * Se o CNPJ está no cadastro do Deca, o painel mostra também o que o cadastro
 * sabe da empresa — colaboradores, contatos do CRM, deságio do fornecedor
 * (`NoCadastroDoDeca`) —, e "Anotar" traz a pessoa para os contatos.
 */
export function AnotacoesDaEmpresa(props: {
  readonly empresa: EmpresaProspectada;
  readonly aoFechar: () => void;
  readonly aoCriarCard: () => void;
}): JSX.Element {
  const { empresa } = props;
  const queryClient = useQueryClient();
  const a = empresa.anotacao;

  const [contatos, setContatos] = useState<ContatoEditavel[]>(() =>
    a.contatos.map((c) => ({
      responsavel: paraTexto(c.responsavel),
      cargo: paraTexto(c.cargo),
      telefone: paraTexto(c.telefone),
      email: paraTexto(c.email),
    })),
  );
  const [desagios, setDesagios] = useState({
    EXPORTACAO: paraTexto(a.desagioExportacao),
    DIFERIMENTO: paraTexto(a.desagioDiferimento),
    INVESTIMENTO: paraTexto(a.desagioInvestimento),
  });
  const [atualizacoes, setAtualizacoes] = useState(paraTexto(a.atualizacoes));

  const historico = useQuery({
    queryKey: [...CHAVE_DA_PROSPECCAO, 'historico', empresa.cnpj],
    queryFn: () => historicoDaEmpresa(empresa.cnpj),
  });
  // Só para quem está no cadastro: as demais não têm o que mostrar.
  const cadastro = useQuery({
    queryKey: [...CHAVE_DA_PROSPECCAO, 'cadastro', empresa.cnpj],
    queryFn: () => carregarCadastroDaEmpresa(empresa.cnpj),
    enabled: empresa.cadastro !== null,
  });

  const dados: AnotacaoParaSalvar = {
    // Linha de contato toda em branco não é contato.
    contatos: contatos
      .map((c) => ({
        responsavel: paraValor(c.responsavel),
        cargo: paraValor(c.cargo),
        telefone: paraValor(c.telefone),
        email: paraValor(c.email),
      }))
      .filter((c) => Object.values(c).some((v) => v !== null)),
    desagioExportacao: paraValor(desagios.EXPORTACAO),
    desagioDiferimento: paraValor(desagios.DIFERIMENTO),
    desagioInvestimento: paraValor(desagios.INVESTIMENTO),
    atualizacoes: paraValor(atualizacoes),
  };
  const [original] = useState(() => JSON.stringify(dados));
  const alterado = JSON.stringify(dados) !== original;

  // "Criar card" salva antes o que foi digitado: o e-mail de um contato recém
  // anotado pode ser justamente o que o cadastro da empresa vai usar.
  const salvar = useMutation({
    mutationFn: async (depois: 'fechar' | 'criar-card') => {
      // Sem mudança, não salva: salvar marca a anotação como editada no Deca,
      // e aí as próximas planilhas deixam de atualizá-la.
      if (alterado) await salvarAnotacao(empresa.cnpj, dados);
      return depois;
    },
    onSuccess: (depois) => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_DA_PROSPECCAO });
      if (depois === 'criar-card') props.aoCriarCard();
      else props.aoFechar();
    },
  });

  /**
   * "Cadastrar em Empresas" (29/10/2026): já como fornecedora, sem card. Salva
   * antes o que foi digitado, pelo mesmo motivo do "Criar card" — o e-mail de
   * um contato recém-anotado pode ser o que o cadastro vai usar.
   */
  const cadastrarEmpresa = useMutation({
    mutationFn: async () => {
      if (alterado) await salvarAnotacao(empresa.cnpj, dados);
      return cadastrarEmpresaDaProspeccao(empresa.id);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_DA_PROSPECCAO }),
  });

  /** "Cadastrar em Pessoas": o contato vira colaborador da empresa, sem CPF. */
  const [emPessoas, setEmPessoas] = useState<readonly string[]>([]);
  const cadastrarPessoa = useMutation({
    mutationFn: (c: ContatoEditavel) =>
      cadastrarPessoaDaProspeccao(empresa.id, {
        nome: c.responsavel.trim(),
        telefone: c.telefone.trim() === '' ? null : c.telefone.trim(),
        email: c.email.trim(),
      }),
    onSuccess: (_r, c) => {
      setEmPessoas((atual) => [...atual, c.email.trim().toLowerCase()]);
      void queryClient.invalidateQueries({ queryKey: CHAVE_DA_PROSPECCAO });
    },
  });
  const jaEmPessoas = (c: ContatoEditavel): boolean => {
    const email = c.email.trim().toLowerCase();
    return (
      email !== '' &&
      (emPessoas.includes(email) ||
        (cadastro.data?.colaboradores ?? []).some((p) => p.email?.toLowerCase() === email))
    );
  };
  const ligarFornecedora =
    empresa.cadastro === null ||
    (cadastro.data !== undefined && cadastro.data !== null && !cadastro.data.perfis.fornecedor);

  const mudarContato = (i: number, campo: keyof ContatoEditavel, valor: string): void =>
    setContatos((atual) => atual.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));

  const identificacao = [
    `CNPJ ${formatarCnpj(empresa.cnpj)}`,
    empresa.cadIcms === null ? null : `CAD/ICMS ${empresa.cadIcms}`,
    empresa.credencial === null ? null : `Credencial ${empresa.credencial}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={empresa.razaoSocial}
      descricao={identificacao}
      largura="ampla"
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao
            carregando={salvar.isPending && salvar.variables === 'fechar'}
            disabled={salvar.isPending}
            onClick={() => salvar.mutate('fechar')}
          >
            Salvar anotações
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda px-4 py-3">
          {empresa.card === null ? (
            <>
              <span className="text-sm text-conteudo-secundario">
                {empresa.cadastro === null
                  ? 'Ainda não está no cadastro nem no CRM.'
                  : 'Está no cadastro, mas não no CRM.'}
              </span>
              <span className="flex flex-wrap gap-2">
                {ligarFornecedora && (
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    carregando={cadastrarEmpresa.isPending}
                    disabled={salvar.isPending}
                    title="Entra em Empresas já como fornecedora, sem card no CRM."
                    onClick={() => cadastrarEmpresa.mutate()}
                  >
                    {empresa.cadastro === null
                      ? 'Cadastrar em Empresas'
                      : 'Marcar como fornecedora'}
                  </Botao>
                )}
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  carregando={salvar.isPending && salvar.variables === 'criar-card'}
                  disabled={salvar.isPending || cadastrarEmpresa.isPending}
                  onClick={() => salvar.mutate('criar-card')}
                >
                  {alterado ? 'Salvar e criar card' : 'Criar card'}
                </Botao>
              </span>
            </>
          ) : (
            <>
              <span className="text-sm text-conteudo-secundario">
                No CRM, na coluna{' '}
                <strong className="text-conteudo-primario">{empresa.card.colunaNome}</strong>.
              </span>
              <span className="flex flex-wrap items-center gap-3">
                {ligarFornecedora && (
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    carregando={cadastrarEmpresa.isPending}
                    onClick={() => cadastrarEmpresa.mutate()}
                  >
                    Marcar como fornecedora
                  </Botao>
                )}
                <Link
                  to={`/crm/pipeline?card=${empresa.card.id}`}
                  className="text-sm font-medium underline underline-offset-2"
                >
                  Abrir card
                </Link>
              </span>
            </>
          )}
        </div>
        {cadastrarEmpresa.isSuccess && (
          <Aviso tom="positivo" titulo="Em Empresas, como fornecedora">
            {cadastrarEmpresa.data.empresaCadastrada
              ? 'A empresa foi cadastrada com os dados do SISCRED e já aparece entre os fornecedores.'
              : 'A empresa já estava no cadastro; agora é fornecedora também.'}{' '}
            <Link
              to={`/empresas/${cadastrarEmpresa.data.businessId}`}
              className="font-medium underline underline-offset-2"
            >
              Abrir em Empresas
            </Link>
          </Aviso>
        )}
        {cadastrarEmpresa.isError && (
          <ErroDaApi titulo="Não foi possível cadastrar" erro={cadastrarEmpresa.error} />
        )}

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-conteudo-primario">No SISCRED, neste mês</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-conteudo-sutil">Telefone</dt>
              <dd className="tabular-nums">{empresa.telefone ?? '—'}</dd>
              <dt className="text-conteudo-sutil">E-mail</dt>
              <dd className="break-all">{empresa.email ?? '—'}</dd>
            </dl>
            {empresa.telefone === null && empresa.email === null && (
              <p className="text-xs text-conteudo-sutil">
                A planilha não trouxe o telefone e o e-mail do extrato: todas as linhas desta
                empresa estavam com contatos anotados.
              </p>
            )}
            <table className="w-full text-sm">
              <caption className="sr-only">Saldos por modalidade</caption>
              <thead>
                <tr className="text-left text-xs text-conteudo-sutil">
                  <th className="py-1 font-medium">Modalidade</th>
                  <th className="py-1 font-medium">Tipo</th>
                  <th className="py-1 text-right font-medium">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda/60">
                {empresa.saldos.map((s, i) => (
                  <tr key={i}>
                    <td className="py-1.5">
                      {MODALIDADES.find((m) => m.chave === s.modalidade)?.rotulo ?? s.modalidade}
                    </td>
                    <td className="py-1.5 text-conteudo-secundario">{s.tipo ?? '—'}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatarMoeda(s.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-borda font-semibold">
                  <td className="py-1.5" colSpan={2}>
                    Total
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatarMoeda(empresa.totalCredito)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-conteudo-primario">
              Crédito nos meses coletados
            </h3>
            {historico.data === undefined ? (
              <p className="text-sm text-conteudo-sutil">
                {historico.isError ? 'Não foi possível carregar.' : 'Carregando…'}
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-borda/60 text-sm">
                {[...historico.data].reverse().map((h) => (
                  <li key={`${h.ano}-${h.mes}`} className="flex justify-between gap-3 py-1.5">
                    <span className="text-conteudo-secundario">
                      {formatarCompetencia(`${h.ano}-${String(h.mes).padStart(2, '0')}`)}
                    </span>
                    <span className="tabular-nums">{formatarMoeda(h.totalCredito)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {empresa.cadastro !== null && (
          <NoCadastroDoDeca
            cadastro={cadastro.data}
            erro={cadastro.error}
            anotados={dados.contatos}
            semEspaco={contatos.length >= 20}
            aoAnotar={(pessoa) =>
              setContatos((atual) => [
                ...atual,
                {
                  responsavel: paraTexto(pessoa.nome),
                  cargo: paraTexto(pessoa.cargo),
                  telefone: paraTexto(pessoa.telefone),
                  email: paraTexto(pessoa.email),
                },
              ])
            }
          />
        )}

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-conteudo-primario">Contatos</h3>
            <Botao
              variante="sutil"
              tamanho="pequeno"
              disabled={contatos.length >= 20}
              onClick={() => setContatos((atual) => [...atual, { ...CONTATO_VAZIO }])}
            >
              Adicionar contato
            </Botao>
          </div>
          {cadastrarPessoa.isError && (
            <ErroDaApi
              titulo="Não foi possível cadastrar em Pessoas"
              erro={cadastrarPessoa.error}
            />
          )}
          {contatos.length === 0 ? (
            <p className="text-sm text-conteudo-sutil">
              Nenhum contato anotado. O telefone e o e-mail acima são os do extrato.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {contatos.map((c, i) => (
                <li
                  key={i}
                  className="grid items-end gap-3 rounded-card border border-borda p-3 sm:grid-cols-2 xl:grid-cols-[1.2fr_1fr_1fr_1.3fr_auto]"
                >
                  <Campo
                    rotulo="Responsável"
                    maxLength={200}
                    value={c.responsavel}
                    onChange={(e) => mudarContato(i, 'responsavel', e.target.value)}
                  />
                  <Campo
                    rotulo="Cargo"
                    maxLength={100}
                    value={c.cargo}
                    onChange={(e) => mudarContato(i, 'cargo', e.target.value)}
                  />
                  <Campo
                    rotulo="Telefone"
                    maxLength={60}
                    value={c.telefone}
                    onChange={(e) => mudarContato(i, 'telefone', e.target.value)}
                  />
                  <Campo
                    rotulo="E-mail"
                    maxLength={255}
                    value={c.email}
                    onChange={(e) => mudarContato(i, 'email', e.target.value)}
                  />
                  <div className="flex items-center justify-end gap-1 sm:col-span-2 xl:col-span-1">
                    {jaEmPessoas(c) ? (
                      <span className="whitespace-nowrap px-2 text-xs text-economia">
                        ✓ Em Pessoas
                      </span>
                    ) : (
                      <Botao
                        variante="sutil"
                        tamanho="pequeno"
                        disabled={
                          empresa.cadastro === null ||
                          c.responsavel.trim() === '' ||
                          c.email.trim() === ''
                        }
                        title={
                          empresa.cadastro === null
                            ? 'Cadastre a empresa em Empresas primeiro.'
                            : c.responsavel.trim() === '' || c.email.trim() === ''
                              ? 'O contato precisa de nome e e-mail.'
                              : 'Entra em Pessoas como colaborador da empresa, sem CPF e sem acesso.'
                        }
                        carregando={cadastrarPessoa.isPending && cadastrarPessoa.variables === c}
                        onClick={() => cadastrarPessoa.mutate(c)}
                      >
                        Cadastrar em Pessoas
                      </Botao>
                    )}
                    <Botao
                      variante="sutil"
                      tamanho="pequeno"
                      onClick={() => setContatos((atual) => atual.filter((_, j) => j !== i))}
                    >
                      Remover
                    </Botao>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-conteudo-primario">Negociação</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            {MODALIDADES.map((m) => (
              <Campo
                key={m.chave}
                rotulo={`Deságio — ${m.rotulo}`}
                placeholder="ex.: 15%"
                maxLength={40}
                value={desagios[m.chave]}
                onChange={(e) => setDesagios((atual) => ({ ...atual, [m.chave]: e.target.value }))}
              />
            ))}
          </div>
          <CampoDeTexto
            rotulo="Atualizações"
            ajuda="O histórico das conversas com a empresa."
            rows={5}
            maxLength={10000}
            value={atualizacoes}
            onChange={(e) => setAtualizacoes(e.target.value)}
          />
          <OrigemDaAnotacao anotacao={a} />
        </section>

        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
      </div>
    </Modal>
  );
}

/** De onde vieram as anotações — e o que isso muda na próxima planilha importada. */
function OrigemDaAnotacao(props: { readonly anotacao: AnotacaoDaEmpresa }): JSX.Element | null {
  const { editadaNoDecaEm, planilhaDe } = props.anotacao;
  if (editadaNoDecaEm !== null) {
    return (
      <p className="text-xs text-conteudo-sutil">
        Editadas no Deca em {new Date(editadaNoDecaEm).toLocaleDateString('pt-BR')}. As próximas
        planilhas só completam o que estiver vazio.
      </p>
    );
  }
  if (planilhaDe !== null) {
    return (
      <p className="text-xs text-conteudo-sutil">
        Vindas da planilha de {rotuloDoMes(planilhaDe.ano, planilhaDe.mes).toLowerCase()}. Ao salvar
        uma edição aqui, o Deca passa a valer sobre as próximas planilhas.
      </p>
    );
  }
  return null;
}
